import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  RadioGroupProps,
} from "@mui/material";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";

type Option = {
  label: string;
  value: string | number;
};

type RHFRadioGroupProps<T extends FieldValues = FieldValues> = {
  name: Path<T>;
  label?: string;
  control: Control<T>;
  options: Option[];
  row?: boolean;
  rules?: RegisterOptions<T, Path<T>>;
  required?: boolean;
} & Omit<RadioGroupProps, "name" | "value" | "onChange">;

export function RHFRadioGroup<T extends FieldValues>({
  name,
  label,
  control,
  options,
  row = false,
  rules,
  required = false,
  ...other
}: RHFRadioGroupProps<T>) {
  const validationRules: RegisterOptions<T, Path<T>> = {
    ...rules,
    ...(required && { required: "This field is required" }),
  };

  return (
    <FormControl component="fieldset">
      {label && <FormLabel component="legend">{label}</FormLabel>}
      <Controller
        name={name}
        control={control}
        rules={validationRules}
        render={({ field, fieldState: { error } }) => (
          <>
            <RadioGroup
              row={row}
              {...field}
              {...other}
              value={field.value ?? ""}
            >
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {error && <FormHelperText error>{error.message}</FormHelperText>}
          </>
        )}
      />
    </FormControl>
  );
}
